import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, AlertTriangle, Loader2, PlusCircle, RefreshCw, GitMerge, Minus } from 'lucide-react';
import { getRun } from '../api/client.js';
import PipelineDiagram from './PipelineDiagram.jsx';

/**
 * Détail d'une exécution — modale centrée sur le viewport (convention UI :
 * portail vers `document.body`, backdrop flouté, fermeture au clic extérieur,
 * par la croix et par Échap).
 *
 * Le corps affiche d'abord le CONTRAT COMMUN (totaux, rejets), donc lisible
 * pour n'importe quel batch. Le bloc `meta` en fin rend le spécifique sans que
 * la console ait à le connaître : c'est ce qui permet d'ajouter un batch sans
 * toucher à ce fichier.
 */
export default function RunDetailModal({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    getRun(runId, ctrl.signal)
      .then(setRun)
      .catch((err) => {
        if (err?.name !== 'AbortError') setError(err);
      });
    return () => ctrl.abort();
  }, [runId]);

  // Fermeture au clavier : une modale qui ne se ferme qu'à la souris est un
  // piège d'accessibilité.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const totals = run?.totals ?? {};
  const rejects = run?.rejects ?? [];
  const items = useMemo(() => run?.items ?? [], [run]);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: 'min(860px, 100%)',
          maxHeight: '86vh',
          overflowY: 'auto',
          background: 'var(--card-bg)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--glass-border)',
          boxShadow: 'var(--shadow-card)',
          padding: 20,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>{run?.label || run?.type || 'Exécution'}</h3>
            {run && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {run.app} · {run.reference || 'sans référence'} ·{' '}
                {new Date(run.startedAt).toLocaleString('fr-CH')}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fermer"
            aria-label="Fermer"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              height: 28,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <p style={{ color: 'var(--danger)', fontSize: 13 }}>
            <AlertTriangle size={14} style={{ verticalAlign: -2 }} /> {error.message}
          </p>
        )}

        {!run && !error && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>
            <Loader2 size={14} style={{ verticalAlign: -2 }} /> Chargement…
          </p>
        )}

        {run && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
                gap: 8,
                margin: '16px 0',
              }}
            >
              {[
                ['Lues', totals.read],
                ['Créés', totals.created],
                ['Mis à jour', totals.updated],
                ['Inchangés', totals.unchanged],
                ['Ignorés', totals.skipped],
                ['Rejets', totals.rejected],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: 10,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--glass)',
                    border: '1px solid var(--glass-border)',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color:
                        label === 'Rejets' && value > 0 ? 'var(--danger)' : 'var(--text)',
                    }}
                  >
                    {value ?? '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
                </div>
              ))}
            </div>

            {run.steps?.length > 0 ? (
              <PipelineDiagram steps={run.steps} />
            ) : (
              <p
                style={{
                  margin: '0 0 16px',
                  padding: 10,
                  fontSize: 12.5,
                  color: 'var(--text-muted)',
                  background: 'var(--glass)',
                  border: '1px dashed var(--glass-border)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                Cette exécution est antérieure à l'enregistrement du détail : ni le
                traitement étape par étape, ni la liste des entités ne sont disponibles.
                Les compteurs ci-dessus restent exacts. Les exécutions suivantes les
                porteront.
              </p>
            )}

            {run.error && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--danger)',
                  color: 'var(--danger)',
                  fontSize: 13,
                  marginBottom: 14,
                }}
              >
                {run.error}
              </div>
            )}

            {rejects.length > 0 && (
              <>
                <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                  Rejets ({run.rejectsTotal ?? rejects.length})
                </h4>
                <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                  <table
                    style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 460 }}
                  >
                    <thead>
                      <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px' }}>Ligne</th>
                        <th style={{ padding: '6px 8px' }}>Référence</th>
                        <th style={{ padding: '6px 8px' }}>Motif</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejects.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--glass-border)' }}>
                          <td style={{ padding: '6px 8px' }}>{r.line ?? '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{r.ref ?? '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {items.length > 0 && (
              <ItemsSection items={items} total={run.itemsTotal ?? items.length} />
            )}

            {run.meta && Object.keys(run.meta).length > 0 && (
              <>
                <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Détail spécifique</h4>
                <pre
                  style={{
                    margin: 0,
                    padding: 12,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--glass)',
                    border: '1px solid var(--glass-border)',
                    fontSize: 12,
                    overflowX: 'auto',
                  }}
                >
                  {JSON.stringify(run.meta, null, 2)}
                </pre>
              </>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Présentation d'une action — vocabulaire COMMUN à tous les batchs. */
const ACTIONS = {
  created: { label: 'Créés', color: 'var(--success)', Icon: PlusCircle },
  updated: { label: 'Mis à jour', color: 'var(--primary)', Icon: RefreshCw },
  merged: { label: 'Rapprochés', color: 'var(--warning)', Icon: GitMerge },
  unchanged: { label: 'Inchangés', color: 'var(--text-muted)', Icon: Minus },
  skipped: { label: 'Ignorés', color: 'var(--text-muted)', Icon: Minus },
};

/**
 * Entités traitées par l'exécution, filtrables par action.
 *
 * Cette section ne connaît AUCUN batch : elle rend `ref`, `label` et `action`,
 * qui font partie du contrat commun. Un import de congés ou une synchronisation
 * d'affaires s'y afficheront sans une ligne de code supplémentaire.
 */
function ItemsSection({ items, total }) {
  // Par défaut on montre les créations : c'est ce qu'on vient vérifier après
  // un import. Les « inchangés » sont majoritaires et noieraient le signal.
  const [filter, setFilter] = useState('created');

  const counts = useMemo(() => {
    const c = {};
    for (const i of items) c[i.action] = (c[i.action] ?? 0) + 1;
    return c;
  }, [items]);

  // Si rien n'a été créé, on ouvre sur l'action la plus représentée.
  const effective = counts[filter] ? filter : Object.keys(counts)[0] ?? filter;
  const shown = items.filter((i) => i.action === effective);
  const truncated = total > items.length;

  return (
    <>
      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
        Entités traitées{' '}
        <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({total})</span>
      </h4>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {Object.entries(counts).map(([action, n]) => {
          const a = ACTIONS[action] ?? { label: action, color: 'var(--text-muted)', Icon: Minus };
          const { Icon } = a;
          const active = effective === action;
          return (
            <button
              key={action}
              type="button"
              onClick={() => setFilter(action)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 11px',
                borderRadius: 'var(--radius-pill)',
                border: `1px solid ${active ? a.color : 'var(--glass-border)'}`,
                background: active ? a.color : 'transparent',
                color: active ? '#fff' : 'var(--text-muted)',
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              <Icon size={12} /> {a.label} · {n}
            </button>
          );
        })}
      </div>

      <div
        style={{
          maxHeight: 260,
          overflowY: 'auto',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <tbody>
            {shown.map((i, idx) => (
              <tr key={`${i.ref}-${idx}`} style={{ borderTop: idx ? '1px solid var(--glass-border)' : 'none' }}>
                <td
                  style={{
                    padding: '6px 10px',
                    width: 90,
                    color: 'var(--text-muted)',
                    fontFamily: 'ui-monospace, monospace',
                  }}
                >
                  {i.ref ?? '—'}
                </td>
                <td style={{ padding: '6px 10px' }}>{i.label ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {truncated && (
        <p style={{ margin: '6px 0 16px', fontSize: 11.5, color: 'var(--text-muted)' }}>
          Liste tronquée : {items.length} entités détaillées sur {total} traitées. Les
          compteurs ci-dessus restent exacts.
        </p>
      )}
      {!truncated && <div style={{ marginBottom: 16 }} />}
    </>
  );
}
